import {SagaIterator} from 'redux-saga';
import {call, put, putResolve, select} from 'redux-saga/effects';

import {STEPS} from 'desktop/pages/checkout/constants/checkout';
import {CustomerAddressType} from 'desktop/types/customerAddressesType';

import {apiConfig} from 'libs/api/api';
import {ActionType} from 'types/reducers';

import {saveEditedCustomerAddresses} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {getOrderReviewTotals} from '../../../reducers/orderReviewTotals/orderReviewTotals';
import {changeStepStatus} from '../../../reducers/status/status';
import {
    getAddressesSameness,
    getCustomerAddresses,
} from '../../../selectors/deliveryStep/deliveryStep';
import {getShippingAddress} from '../../../selectors/shippingAddress/shippingAddress';
import {getSavedCustomerAddressesSaga} from '../deliveryStepSaga/deliveryStepSaga';
import {getShippingMethodsSaga} from '../getShippingMethodsSaga/getShippingMethodsSaga';
import {saveCustomerAddressSaga} from '../saveCustomerAddressSaga/saveCustomerAddressSaga';

import {makeFormData} from './utils/makeFormData/makeFormData';

/**
 * Should update customer data on address change to get proper taxes
 * @param {CustomerAddressType} selectedAddress - address that use choose from dropdown
 * @param {string} type - shipping or billing address type
 */
export function* updateCustomerDataOnAddressChange(
    selectedAddress: CustomerAddressType,
    type: string,
): SagaIterator {
    const currentShippingAddress = yield select(getShippingAddress);
    const isAddressSameness = yield select(getAddressesSameness);

    // Getting actual addresses list
    yield call(getSavedCustomerAddressesSaga);

    // Save selected address to getQuote to get proper taxes
    // This will be not needed when backend will bind saveEditedCustomerAddress endpoint with saveCustomerAddress logic
    // No estimation here
    yield call(saveCustomerAddressSaga);

    // Getting new shipping methods for new address if it has new country
    if (
        (type === 'shipping' || isAddressSameness) &&
        currentShippingAddress.country.value !== selectedAddress.country
    ) {
        yield call(getShippingMethodsSaga);
    }

    // Getting new totals to refresh taxes
    yield putResolve(getOrderReviewTotals({collectTax: true, withTaxErrors: false}));

    yield put(
        changeStepStatus({
            [STEPS.DELIVERY_STEP]: {
                loaded: true,
            },
        }),
    );
}

/**
 * Update customer address
 * @param {{payload: {id: string, type: string}}} param - Address information to save
 */
export function* updateCustomerAddressSaga({
    payload: {id, type},
}: ActionType<{id: string; type: string}>): SagaIterator {
    yield put(
        changeStepStatus({
            [STEPS.DELIVERY_STEP]: {
                loaded: false,
            },
        }),
    );

    const {addresses} = yield select(getCustomerAddresses);
    const isAddressesSame = yield select(getAddressesSameness);
    const {id: customerId} = apiConfig.hostStore.getState().customer;

    // TODO GUSA-12485 Fix all address.addressId & shipping_address_id types because now its number (not strings)
    const selectedAddress = addresses.find(
        (address: CustomerAddressType) => String(address.addressId) === String(id),
    );

    // Making form data for request
    const formData = yield call(makeFormData, selectedAddress, {customerId, isAddressesSame, type});

    // Save updated customer address
    yield putResolve(saveEditedCustomerAddresses({data: formData, id}));

    yield call(updateCustomerDataOnAddressChange, selectedAddress, type);
}
